import type * as NativeFiles from "./postPublishFiles";
import { MAX_POST_IMAGE_BYTES, MAX_POST_PDF_BYTES, createPostUploadError } from "./postAttachments";
import type { PostPublishAttachment } from "./postPublishTypes";
import {
  getBrowserPostFile,
  registerBrowserPostFile,
  releaseBrowserPostFile,
  releaseBrowserPostJob,
} from "./webPostFiles";

const MAX_IMAGE_SIDE = 1400;
const IMAGE_QUALITY = 0.82;
const WEB_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function imageType(blob: Blob, name?: string) {
  if (WEB_IMAGE_TYPES.has(blob.type)) return blob.type;
  const extension = String(name ?? "").split(".").at(-1)?.toLowerCase();
  if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
  if (extension === "png") return "image/png";
  if (extension === "webp") return "image/webp";
  return "";
}

async function sourceBlob(uri: string) {
  const tracked = getBrowserPostFile(uri);
  if (tracked) return tracked;
  const response = await fetch(uri);
  if (!response.ok) throw createPostUploadError("post/file-read-failed", "Le fichier n'a pas pu etre lu.");
  return response.blob();
}

async function decodeImage(blob: Blob) {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw: (context: CanvasRenderingContext2D, width: number, height: number) => context.drawImage(bitmap, 0, 0, width, height),
        close: () => bitmap.close(),
      };
    } catch {
      // Some WebViews expose createImageBitmap but reject its orientation option
      // (or a valid format). The regular image decoder remains widely supported.
    }
  }
  const uri = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new window.Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(createPostUploadError("post/image-manipulation-failed", "Cette image ne peut pas etre ouverte."));
      element.src = uri;
    });
    return {
      width: image.naturalWidth,
      height: image.naturalHeight,
      draw: (context: CanvasRenderingContext2D, width: number, height: number) => context.drawImage(image, 0, 0, width, height),
      close: () => {},
    };
  } finally {
    URL.revokeObjectURL(uri);
  }
}

async function normalizeBrowserImage(blob: Blob) {
  let decoded: Awaited<ReturnType<typeof decodeImage>>;
  try {
    decoded = await decodeImage(blob);
  } catch (error) {
    if ((error as { code?: string }).code) throw error;
    throw createPostUploadError("post/image-manipulation-failed", "Cette image ne peut pas etre ouverte.", error);
  }
  try {
    if (!decoded.width || !decoded.height) {
      throw createPostUploadError("post/image-manipulation-failed", "Les dimensions de l'image sont invalides.");
    }
    const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(decoded.width, decoded.height));
    const width = Math.max(1, Math.round(decoded.width * scale));
    const height = Math.max(1, Math.round(decoded.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw createPostUploadError("post/image-manipulation-failed", "La conversion de l'image est indisponible.");
    decoded.draw(context, width, height);
    const normalized = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", IMAGE_QUALITY));
    if (!normalized?.size) throw createPostUploadError("post/image-manipulation-failed", "La conversion de l'image a echoue.");
    if (normalized.size > MAX_POST_IMAGE_BYTES) throw createPostUploadError("post/file-too-large", "La photo normalisee depasse 10 Mio.");
    return { blob: normalized, width, height };
  } finally {
    decoded.close();
  }
}

export const prepareDurableImage: typeof NativeFiles.prepareDurableImage = async (params) => {
  const startedAt = (params.now ?? Date.now)();
  const blob = await sourceBlob(params.sourceUri);
  if (!blob.size || blob.size > MAX_POST_IMAGE_BYTES) {
    throw createPostUploadError("post/file-too-large", "La photo depasse 10 Mio.");
  }
  if (!imageType(blob, params.name)) {
    throw createPostUploadError("post/image-manipulation-failed", "Utilise une image JPEG, PNG ou WebP.");
  }
  const normalized = await normalizeBrowserImage(blob);
  const uri = registerBrowserPostFile(params.jobId, normalized.blob);
  return {
    attachmentId: params.attachmentId,
    order: 0,
    type: "image",
    status: "ready",
    durableUri: uri,
    previewUri: uri,
    name: `${params.attachmentId}.jpg`,
    contentType: "image/jpeg",
    originalSize: params.size ?? blob.size,
    normalizedSize: normalized.blob.size,
    width: normalized.width,
    height: normalized.height,
    preparationDurationMs: (params.now ?? Date.now)() - startedAt,
  };
};

export const prepareDurablePdf: typeof NativeFiles.prepareDurablePdf = async (params) => {
  const blob = await sourceBlob(params.sourceUri);
  const size = params.size ?? blob.size;
  if (!size || size > MAX_POST_PDF_BYTES) throw createPostUploadError("post/file-too-large", "Le PDF depasse 20 Mio.");
  const header = new TextDecoder("ascii").decode(await blob.slice(0, 5).arrayBuffer());
  if (blob.type !== "application/pdf" || header !== "%PDF-") {
    throw createPostUploadError("post/file-read-failed", "Le fichier selectionne n'est pas un PDF valide.");
  }
  const uri = registerBrowserPostFile(params.jobId, blob);
  return {
    attachmentId: params.attachmentId,
    order: 0,
    type: "pdf",
    status: "ready",
    durableUri: uri,
    previewUri: uri,
    name: params.name,
    contentType: "application/pdf",
    originalSize: size,
    normalizedSize: size,
  };
};

export const durableAttachmentExists = (uri: string) => !!getBrowserPostFile(uri);
export const releasePostPublishAttachment = (attachment?: Pick<PostPublishAttachment, "durableUri"> | null) => {
  if (attachment?.durableUri) releaseBrowserPostFile(attachment.durableUri);
};
export const deletePostPublishJobFiles = (jobId: string) => releaseBrowserPostJob(jobId);
export const cleanupOrphanedPostPublishDirectories = (_activeJobIds: Set<string>) => {};
